import React from "react";
import ModalDialog from "src/@components/modal.dialog";
import { CTAButtonV2 } from "src/components/ctabutton";
import ImageV2 from "src/components/image/image.v2";
import { convertToHTML } from "src/utils/string";
import {
  getOnlineInterstitialCommunications,
  getSiteElapsedTimeMs,
  markCommunicationAsSeen,
  wasCommunicationSeen
} from "./onlineInterstitialCommunication.service";
import { TOnlineInterstitialCommunication, TOnlineInterstitialTranslation } from "./onlineInterstitialCommunication.types";

type Props = {
  language: string;
  defaultLanguage: string;
  layoutId: string | number;
}

type State = {
  open: boolean;
  communication: TOnlineInterstitialCommunication | null;
}

export default class OnlineInterstitialCommunication extends React.Component<Props, State> {
  private timer: ReturnType<typeof setTimeout> | undefined;

  state: State = {
    open: false,
    communication: null,
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.language !== this.props.language || prevProps.layoutId !== this.props.layoutId) {
      this.clearTimer();
      this.setState({ open: false, communication: null }, this.load);
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  clearTimer = () => {
    if (this.timer) clearTimeout(this.timer);
  }

  load = async () => {
    try {
      const communications = await getOnlineInterstitialCommunications(this.props.language, this.props.layoutId);
      const communication = communications
        .filter(item => this.isEligible(item))
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))[0];

      if (!communication) return;

      const delayMs = Math.max((communication.trigger_delay_seconds || 0) * 1000, 0);
      const remainingDelayMs = Math.max(delayMs - getSiteElapsedTimeMs(), 0);

      this.timer = setTimeout(() => this.open(communication), remainingDelayMs);
    } catch (err) {}
  }

  isEligible(communication: TOnlineInterstitialCommunication) {
    if (!communication?.id) return false;
    if (communication.status && communication.status !== "published") return false;
    if (communication.trigger_type && communication.trigger_type !== "time_on_site") return false;
    if (wasCommunicationSeen(communication)) return false;

    const translation = this.getTranslation(communication);
    return Boolean(translation?.title || translation?.body || communication.image || communication.cta);
  }

  getTranslation(communication: TOnlineInterstitialCommunication | null): TOnlineInterstitialTranslation | null {
    if (!communication?.translations) return null;
    if (!Array.isArray(communication.translations)) return communication.translations;

    return communication.translations.find(translation => translation.languages_code === this.props.language)
      || communication.translations.find(translation => translation.languages_code === this.props.defaultLanguage)
      || communication.translations[0]
      || null;
  }

  open = (communication: TOnlineInterstitialCommunication) => {
    if (wasCommunicationSeen(communication)) return;
    this.setState({ communication, open: true });
  }

  close = () => {
    this.markAsSeen();
    this.setState({ open: false });
  }

  markAsSeen = () => {
    if (this.state.communication) markCommunicationAsSeen(this.state.communication);
  }

  renderImage(communication: TOnlineInterstitialCommunication) {
    const image = communication.image;
    const src = image?.imageFile || image?.filename_disk;
    if (!src) return null;

    return <ImageV2
      src={src}
      alt={image?.title || ""}
      useImageKit={true}
      width={960}
      className="w-full max-h-[16rem] object-cover"
    />
  }

  render() {
    const communication = this.state.communication;
    const translation = this.getTranslation(communication);
    if (!communication) return null;

    return <ModalDialog
      open={this.state.open}
      onClose={this.close}
      title={translation?.title || ""}
      size="medium"
      fillHeight={false}
      alignedCenter={true}
      removePaddings={true}
      noScroll={true}
    >
      <div className="flex flex-col max-h-[calc(100vh_-_14rem)] overflow-y-auto">
        {this.renderImage(communication)}
        <div className="p-6">
          {translation?.body ? <div className="html generalText">{convertToHTML(translation.body)}</div> : null}
          {communication.cta ? <div className="mt-6">
            <CTAButtonV2
              ctabutton={communication.cta}
              language={this.props.language}
              defaultLanguage={this.props.defaultLanguage}
              className="w-full justify-center"
              onClick={this.markAsSeen}
            />
          </div> : null}
        </div>
      </div>
    </ModalDialog>
  }
}
